import ToolbarButton from "../toolbar/ToolbarButton";
import { Layer } from "../../types/layer";
import { Feature, MultiPolygon, Polygon } from "geojson";

const MapProjectBoundingBoxLayer = (
  id: string,
  name: string,
  showName: boolean = false,
  fill: boolean = false,
  active: boolean = true,
  stale: boolean = true,
  setStale: React.Dispatch<React.SetStateAction<boolean>> = () => {},
  geojson: Feature<Polygon | MultiPolygon> | null = null,
  toggleActive: () => void = () => {},
  disabled: boolean = false,
  // Amber emphasis for projects with open detection incidents.
  highlighted: boolean = false
): Layer => {
  // Modern gradient fill with better colors
  const fillLayerStyle = fill
    ? {
        id: `${id}-fill`,
        type: "fill",
        paint: {
          "fill-color": disabled 
            ? "#64748b" // Slate gray when disabled
            : [
                "interpolate",
                ["linear"],
                ["zoom"],
                5, "#3b82f6", // Blue at low zoom
                10, "#6366f1", // Indigo at mid zoom
                15, "#8b5cf6"  // Purple at high zoom
              ],
          "fill-opacity": disabled 
            ? 0.12 
            : [
                "interpolate",
                ["linear"],
                ["zoom"],
                5, 0.25,  // More visible at low zoom
                10, 0.20,
                15, 0.15  // More subtle at high zoom
              ],
        },
      }
    : null;

  // Enhanced border with glow effect
  const lineLayerStyle = {
    id: `${id}-line`,
    type: "line",
    layout: {
      "line-join": "round",
      "line-cap": "round",
    },
    paint: {
      "line-color": disabled
        ? "#94a3b8" // Slate-400 when disabled
        : highlighted
        ? "#f59e0b" // Amber: open incidents
        : [
            "interpolate",
            ["linear"],
            ["zoom"],
            5, "#60a5fa",  // Sky-400
            10, "#818cf8", // Indigo-400
            15, "#a78bfa"  // Purple-400
          ],
      "line-width": disabled
        ? 1.5
        : [
            "interpolate",
            ["linear"],
            ["zoom"],
            5, highlighted ? 3.5 : 2.5,
            10, highlighted ? 3 : 2,
            15, highlighted ? 2.5 : 1.5
          ],
      "line-blur": disabled ? 0 : 0.5, // Subtle glow effect
      "line-opacity": disabled ? 0.5 : highlighted ? 1 : 0.9,
    },
  };

  // Add an outer glow layer for depth (only when not disabled)
  const glowLayerStyle = !disabled && fill
    ? {
        id: `${id}-glow`,
        type: "line",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": highlighted
            ? "#f59e0b"
            : [
                "interpolate",
                ["linear"],
                ["zoom"],
                5, "#3b82f6",
                10, "#6366f1",
                15, "#8b5cf6"
              ],
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            5, 8,
            10, 6,
            15, 4
          ],
          "line-blur": 4,
          "line-opacity": highlighted ? 0.4 : 0.2,
        },
      }
    : null;

  // Enhanced text layer with better typography
  const textLayerStyle = showName
    ? {
        id: `${id}-text`,
        type: "symbol",
        layout: {
          "text-field": name,
          "text-size": [
            "interpolate",
            ["linear"],
            ["zoom"],
            5, 14,
            10, 16,
            15, 18
          ],
          "text-font": ["DIN Pro Medium", "Arial Unicode MS Bold"],
          "text-offset": [0, 0.5],
          "text-anchor": "top",
          "text-allow-overlap": true,
          "text-ignore-placement": true,
          "symbol-placement": "point",
          "symbol-avoid-edges": false,
          "text-letter-spacing": 0.05,
          "text-transform": "uppercase",
        },
        paint: {
          "text-color": disabled ? "#94a3b8" : "#ffffff",
          "text-halo-color": disabled 
            ? "rgba(15, 23, 42, 0.8)" // Slate-900 with opacity
            : "rgba(15, 23, 42, 0.95)",
          "text-halo-width": 2,
          "text-halo-blur": 0.5,
        },
      }
    : null;

  const toolbarButton = (
    <ToolbarButton
      key={id}
      id={id}
      name={name}
      toggledOn={active}
      enabled={!disabled && !!geojson}
      onClick={toggleActive}
      icon={"layers"}
    />
  );

  const layer: Layer = {
    id: id,
    name: name,
    source: geojson,
    layerStyle: [
      ...(glowLayerStyle ? [glowLayerStyle] : []),
      ...(fillLayerStyle ? [fillLayerStyle] : []),
      lineLayerStyle,
      ...(textLayerStyle ? [textLayerStyle] : []),
    ],
    toolbarButton: toolbarButton,
    active: active,
    stale: stale,
    setStale: setStale,
    type: "project",
    interactiveLayerIds: disabled ? [] : [
      ...(fillLayerStyle ? [`${id}-fill`] : []),
      `${id}-line`,
      ...(glowLayerStyle ? [`${id}-glow`] : []),
    ],
  };

  return layer;
};

export default MapProjectBoundingBoxLayer;
